import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from '@tanstack/react-router'
import './index.css'
import { queryClient } from './lib/queryClient'
import { router } from './router'
import { Toaster } from '@/components/ui/toaster'
import { PWAUpdatePrompt } from '@/components/PWAUpdatePrompt'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { SignalRProvider } from './contexts/SignalRContext'
import { PushNotificationSyncProvider } from './contexts/PushNotificationSyncContext'
import { buildApiUrl } from './lib/config'

// Global error handling for development debugging
if (import.meta.env.DEV) {
  window.addEventListener('unhandledrejection', (event) => {
    console.warn('Unhandled promise rejection:', event.reason);
  });
}

// Handle notification mark-as-read from cold start (URL parameter)
// When the PWA is completely closed and a notification is clicked,
// the service worker can't postMessage to a non-existent window,
// so it passes the notificationId via URL parameter instead.
const handleColdStartNotification = async () => {
  const url = new URL(window.location.href);
  const notificationId = url.searchParams.get('markNotificationRead');

  if (notificationId) {
    // Remove the parameter from URL to prevent re-triggering on refresh
    url.searchParams.delete('markNotificationRead');
    window.history.replaceState({}, '', url.toString());

    // Mark notification as read
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        await fetch(buildApiUrl(`notifications/${notificationId}/read`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          keepalive: true
        });
      } catch (error) {
        console.warn('Failed to mark notification as read:', error);
      }
    }
  }
};

handleColdStartNotification();

// Listen for service worker messages (push notification clicks)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', async (event) => {
    if (event.data?.type === 'NOTIFICATION_CLICK') {
      const { url, notificationId } = event.data;

      // Mark notification as read if we have the ID
      if (notificationId) {
        const token = localStorage.getItem('authToken');
        if (token) {
          try {
            await fetch(buildApiUrl(`notifications/${notificationId}/read`), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              keepalive: true
            });
          } catch (error) {
            console.warn('Failed to mark notification as read:', error);
          }
        }
      }

      // Navigate to the URL
      if (url) {
        window.location.href = url;
      }
    }
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PushNotificationSyncProvider>
          <SignalRProvider>
            <ThemeProvider>
              <RouterProvider router={router} context={{ queryClient }} />
              <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
              <Toaster />
              <PWAUpdatePrompt />
            </ThemeProvider>
          </SignalRProvider>
        </PushNotificationSyncProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
